import { useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider.js';
import { C, mix } from '../lib/theme.js';
import { Button, ErrorLine, Eyebrow } from '../ui/ui.js';
import basilLogo from '../assets/logo_basil.png';

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function LoginPage() {
  const { signInWithEmail, signInWithPassword, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithPassword(email, password);
      // No setSession here — onAuthStateChange in AuthProvider picks it up.
    } catch (err) {
      // Supabase returns the same "Invalid login credentials" for a wrong password
      // and an email with no password set, which is exactly right: it tells someone
      // probing the form nothing about who has an account.
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  async function onMagicLink() {
    if (!email) {
      setError('Enter your email first.');
      return;
    }
    setError(null);
    setLinkBusy(true);
    try {
      await signInWithEmail(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send sign-in link');
    } finally {
      setLinkBusy(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setGoogleBusy(true);
    try {
      await signInWithGoogle(); // redirects away on success
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
      setGoogleBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(120% 120% at 50% -10%, ${mix(C.basil, 14, C.base)} 0%, ${C.base} 55%)`,
        padding: 24,
      }}
    >
      <div style={{ width: 'min(400px,100%)' }}>
        <img
          src={basilLogo}
          alt="Basil"
          style={{ width: 52, height: 52, objectFit: 'contain', marginBottom: 24 }}
        />

        {sent ? (
          <>
            <Eyebrow style={{ marginBottom: 10 }}>Magic link sent</Eyebrow>
            <h1 className="page-title" style={{ marginBottom: 12 }}>
              Check your email
            </h1>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6 }}>
              We sent a sign-in link to <span style={{ color: C.primary }}>{email}</span>. Open it
              on this device to continue.
            </p>
          </>
        ) : (
          <>
            <Eyebrow style={{ marginBottom: 10 }}>Meta ads, curated</Eyebrow>
            <h1 className="page-title" style={{ fontSize: 32, marginBottom: 10 }}>
              Basil
            </h1>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
              Sign in to continue.
            </p>

            <button
              type="button"
              onClick={() => void onGoogle()}
              disabled={googleBusy}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '11px',
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 10,
                cursor: googleBusy ? 'default' : 'pointer',
                background: '#fff',
                color: '#1f1f1f',
                border: '1px solid rgba(0,0,0,.12)',
              }}
            >
              <GoogleG />
              {googleBusy ? 'Redirecting…' : 'Continue with Google'}
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '18px 0',
                color: C.faint,
                font: `500 10px 'IBM Plex Mono',monospace`,
                letterSpacing: '.1em',
              }}
            >
              <span style={{ flex: 1, height: 1, background: C.line }} />
              OR EMAIL
              <span style={{ flex: 1, height: 1, background: C.line }} />
            </div>

            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                className="field"
                type="email"
                required
                autoComplete="username"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ fontSize: 14, padding: '11px 13px' }}
              />
              <input
                className="field"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ fontSize: 14, padding: '11px 13px' }}
              />
              <Button
                variant="primary"
                type="submit"
                disabled={busy}
                style={{ padding: '11px', fontSize: 14 }}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            {error && <ErrorLine>{error}</ErrorLine>}

            {/* Kept as the recovery path: accounts have no password reset by design,
                so this is how someone with a Google-less account gets in if their
                password is lost. Needs only the email field filled. */}
            <button
              type="button"
              onClick={() => void onMagicLink()}
              disabled={linkBusy}
              style={{
                marginTop: 14,
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                color: C.muted,
                fontSize: 12,
                cursor: linkBusy ? 'default' : 'pointer',
                textDecoration: 'underline',
              }}
            >
              {linkBusy ? 'Sending…' : 'Email me a sign-in link instead'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
